<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900">Purchasing Reports</h1>
    </div>

    <div v-if="!canView" class="mt-10"></div>

    <template v-else>
      <div class="px-5 pt-4">
        <div class="flex justify-center">
          <div class="inline-flex flex-wrap items-center gap-6">
            <button
              class="h-9 px-4 text-lg font-semibold transition"
              :class="activeTab==='invoices' ? 'text-gray-900' : 'text-gray-400'"
              @click="activeTab='invoices'"
            >
              Petty Cash Report
            </button>
            <button
              class="h-9 px-4 text-lg font-semibold transition"
              :class="activeTab==='statement' ? 'text-gray-900' : 'text-gray-400'"
              @click="activeTab='statement'"
            >
              Petty Cash Statement
            </button>
          </div>
        </div>
      </div>

      <div class="p-5 sm:p-6">
        <!-- TAB 1 -->
        <div v-show="activeTab==='invoices'">
          <DynamicReportForm
            :columns="2"
            :stackAt="'md'"
            :contentWidth="'xl'"
            :buttonClass="'w-full md:w-[360px]'"
            v-model="form1"
            :fields="fields1"
            :loading="loading1"
            @submit="runReport1"
          />
        </div>

        <!-- TAB 2 -->
        <div v-show="activeTab==='statement'">
          <DynamicReportForm
            :columns="2"
            :stackAt="'md'"
            :contentWidth="'xl'"
            :buttonClass="'w-full md:w-[360px]'"
            v-model="form2"
            :fields="fields2"
            :loading="loading2"
            @submit="runReport2"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, watch, watchEffect, onMounted } from 'vue'
import DynamicReportForm from '@/components/DynamicReportForm.vue'
import { loadMyPermissions, hasAny } from '@/utils/permissions'
import api from '@/api'
import Swal from 'sweetalert2'

const canView   = ref(false)
const activeTab = ref('invoices')

const companiesOptions = ref([])

onMounted(async () => {
  await loadMyPermissions(false)
  canView.value = hasAny(['purchasing_fund_tabs_report.view'])
  try {
    const res = await api.get('/accounting/companies')
    companiesOptions.value = (res.data ?? []).map(c => ({ label: c.name, value: c.id }))
  } catch (e) {
    console.error('Error fetching companies:', e)
  }
})

// ===== TAB 1 =====
const form1    = ref({ company_id: '', petty_cash_id: '', from: '', to: '' })
const loading1 = ref(false)

const pettyCashOptions1 = ref([])
const pettyCashLoading1 = ref(false)

watch(() => form1.value.company_id, async (companyId) => {
  form1.value.petty_cash_id = ''
  pettyCashOptions1.value   = []
  if (!companyId) return

  pettyCashLoading1.value = true
  try {
    const res  = await api.get('/accounting/purchasing-fund/petty-cash', {
      params: { company_id: companyId },
    })
    const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? [])
    pettyCashOptions1.value = data.map(p => ({
      label: p.employee_name || `Petty Cash #${p.id}`,
      value: p.id,
    }))
  } catch (e) {
    console.error('Error fetching petty cash:', e)
  } finally {
    pettyCashLoading1.value = false
  }
})

const fields1 = ref([
  {
    name: 'company_id', label: 'Company', type: 'select',
    placeholder: 'Select company', span: 2,
    options: [],
  },
  {
    name: 'petty_cash_id', label: 'Petty Cash', type: 'select',
    placeholder: 'Select petty cash', span: 2,
    options: [],
  },
  { name: 'from', label: 'From', type: 'date', span: 1 },
  { name: 'to',   label: 'To',   type: 'date', span: 1 },
])

watchEffect(() => {
  fields1.value[0].options = companiesOptions.value
})

watchEffect(() => {
  fields1.value[1].options     = pettyCashOptions1.value
  fields1.value[1].placeholder = pettyCashLoading1.value ? 'Loading...' : 'Select petty cash'
})

async function runReport1(payload) {
  if (!canView.value) return

  // validation
  if (!payload.company_id) {
    Swal.fire({ icon: 'warning', title: 'Please select a company', toast: true, position: 'top', timer: 2000, showConfirmButton: false })
    return
  }
  if (!payload.from || !payload.to) {
    Swal.fire({ icon: 'warning', title: 'Please select date range', toast: true, position: 'top', timer: 2000, showConfirmButton: false })
    return
  }

  const params = new URLSearchParams()
  if (payload.company_id)    params.append('company_id',    payload.company_id)
  if (payload.petty_cash_id) params.append('petty_cash_id', payload.petty_cash_id)
  if (payload.from)          params.append('from_date',     payload.from)
  if (payload.to)            params.append('to_date',       payload.to)

  loading1.value = true
  try {
    const res = await api.get(`/accounting/reports/petty-cash/pdf?${params.toString()}`, {
      responseType: 'blob',
    })
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
    window.open(url, '_blank')
  } catch (err) {
    console.error('Failed to generate report:', err)
    Swal.fire({ icon: 'error', title: 'Failed to generate report', toast: true, position: 'top', timer: 2000, showConfirmButton: false })
  } finally {
    loading1.value = false
  }
}

// ===== TAB 2 =====
const form2    = ref({ company_id: '', from: '', to: '' })
const loading2 = ref(false)

const fields2 = ref([
  {
    name: 'company_id', label: 'Company', type: 'select',
    placeholder: 'Select company', span: 'full',
    options: [],
  },
  { name: 'from', label: 'From', type: 'date', span: 'full' },
  { name: 'to',   label: 'To',   type: 'date', span: 'full' },
])

watchEffect(() => {
  fields2.value[0].options = companiesOptions.value
})

async function runReport2(payload) {
  if (!canView.value) return

  // validation
  if (!payload.from || !payload.to) {
    Swal.fire({ icon: 'warning', title: 'Please select date range', toast: true, position: 'top', timer: 2000, showConfirmButton: false })
    return
  }

  const params = new URLSearchParams()
  if (payload.company_id) params.append('company_id', payload.company_id)
  if (payload.from)       params.append('from_date',  payload.from)
  if (payload.to)         params.append('to_date',    payload.to)

  loading2.value = true
  try {
    const res = await api.get(`/accounting/reports/petty-cash/statement/pdf?${params.toString()}`, {
      responseType: 'blob',
    })
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
    window.open(url, '_blank')
  } catch (err) {
    console.error('Failed to generate statement:', err)
    Swal.fire({ icon: 'error', title: 'Failed to generate report', toast: true, position: 'top', timer: 2000, showConfirmButton: false })
  } finally {
    loading2.value = false
  }
}
</script>
